// apolo/src/lib/loop/oracleGate.js — P4-T02 (code-tab-lift).
// @arch:pure-engine
//
// PURE decision for the AUTO_CORRECT oracle gate, extracted from the loop. The loop
// still OWNS the impure parts — selecting verifiers, the async runOracle() call, and
// applying the side effects (markStage / onEvent / push / continue / return). This
// module answers only "given the oracle's verdict, the attempt count, and the cap,
// what should happen next?" as a pure, unit-testable descriptor.
//
// L-4 SACRED: classifyOracleStep + buildFixNudge are imported READ-ONLY from the
// byte-identical oracle.js mirror — the decoupling is a SEAM on the agentLoop side,
// never an edit to oracle.js. classifyOracleStep stays the SOLE judge (the same fn
// deterministic replay re-derives the outcome with), so the live loop and a replay
// can never drift.
import { classifyOracleStep, buildFixNudge } from '../oracle/oracle.js';

/**
 * @param {{ reachedGreen:boolean, noVerifiers:boolean, verdicts:any[], failed:any[] }} oracleResult
 * @param {{ attempts:number, cap:number }} ctx
 * @returns {{ outcome:'green', autoCorrectResult:object, greenVerifiers:string, unverified:boolean }
 *   | { outcome:'abort', autoCorrectResult:object, stopReason:'autocorrect_cap', failedVerifiers:string, findings:any[] }
 *   | { outcome:'retry', fixNudge:string, failedVerifiers:string, findings:any[], unverified:boolean }}
 */
export function planOracleStep(oracleResult, { attempts, cap }) {
  const step = classifyOracleStep({
    reachedGreen: oracleResult.reachedGreen,
    noVerifiers: oracleResult.noVerifiers,
    attempts,
    cap,
  });
  // T01 (tier-s-sprint-unified-2026-06-28) — classifyOracleStep now returns
  // { outcome, unverified } (honest-green signal). Preserve outcome here.
  const stepOutcome = typeof step === 'string' ? step : step.outcome;
  const stepUnverified = typeof step === 'string' ? false : !!step.unverified;
  if (stepOutcome === 'green') {
    return {
      outcome: 'green',
      autoCorrectResult: {
        attempts,
        capHit: false,
        reachedGreen: true,
        unverified: stepUnverified,
        verdicts: oracleResult.verdicts,
      },
      greenVerifiers: oracleResult.verdicts.map((v) => v.verifier).join(','),
      unverified: stepUnverified,
    };
  }
  const failedVerifiers = oracleResult.failed.map((v) => v.verifier).join(',');
  if (stepOutcome === 'abort') {
    return {
      outcome: 'abort',
      autoCorrectResult: {
        attempts,
        capHit: true,
        reachedGreen: false,
        unverified: false,
        verdicts: oracleResult.verdicts,
      },
      stopReason: 'autocorrect_cap',
      failedVerifiers,
      findings: oracleResult.failed,
    };
  }
  return { outcome: 'retry', fixNudge: buildFixNudge(oracleResult), failedVerifiers, findings: oracleResult.failed, unverified: false };
}

export default planOracleStep;
